/**
 * shelf-indulgence.js
 * Shelf Indulgence — Custom theme JS
 * Mega menu, mobile drawer, newsletter, add-to-basket feedback
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------------
   * 1. MEGA MENU
   * Books and Board Games nav items trigger a 4-col dropdown.
   * 120ms close delay prevents accidental dismissal.
   * ---------------------------------------------------------------- */

  const SIMegaMenu = {
    closeDelay: 120,
    activeItem: null,
    closeTimer: null,

    init() {
      document.querySelectorAll('[data-si-mega-trigger]').forEach((trigger) => {
        const menuId = trigger.dataset.siMegaTrigger;
        const menu = document.getElementById(menuId);
        if (!menu) return;

        trigger.addEventListener('mouseenter', () => this.open(trigger, menu));
        trigger.addEventListener('mouseleave', () => this.scheduleClose(menu));
        menu.addEventListener('mouseenter', () => this.cancelClose());
        menu.addEventListener('mouseleave', () => this.scheduleClose(menu));

        // Keyboard: open on Enter/Space, close on Escape
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (menu.classList.contains('is-open')) {
              this.close(trigger, menu);
            } else {
              this.open(trigger, menu);
            }
          }
          if (e.key === 'Escape') this.close(trigger, menu);
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && menu.classList.contains('is-open')) {
            this.close(trigger, menu);
            trigger.focus();
          }
        });
      });

      // Click outside to close
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-si-mega-trigger]') && !e.target.closest('.si-mega-menu')) {
          document.querySelectorAll('.si-mega-menu.is-open').forEach((m) => {
            const trigger = document.querySelector(`[data-si-mega-trigger="${m.id}"]`);
            if (trigger) this.close(trigger, m);
          });
        }
      });
    },

    open(trigger, menu) {
      this.cancelClose();

      // Close any other open menus first
      document.querySelectorAll('.si-mega-menu.is-open').forEach((m) => {
        if (m !== menu) {
          const t = document.querySelector(`[data-si-mega-trigger="${m.id}"]`);
          if (t) this.close(t, m);
        }
      });

      menu.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.closest('.si-nav__item')?.classList.add('si-nav__item--open');

      // Add nav shadow
      const header = trigger.closest('.si-header');
      if (header) header.style.boxShadow = 'var(--shadow-nav)';
    },

    close(trigger, menu) {
      menu.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.closest('.si-nav__item')?.classList.remove('si-nav__item--open');

      const header = trigger.closest('.si-header');
      if (header) header.style.boxShadow = '';
    },

    scheduleClose(menu) {
      this.cancelClose();
      this.closeTimer = setTimeout(() => {
        const trigger = document.querySelector(`[data-si-mega-trigger="${menu.id}"]`);
        if (trigger) this.close(trigger, menu);
      }, this.closeDelay);
    },

    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    },
  };

  /* ----------------------------------------------------------------
   * 2. MOBILE MENU DRAWER
   * ---------------------------------------------------------------- */

  const SIMobileMenu = {
    init() {
      const openBtns = document.querySelectorAll('[data-si-mobile-menu-open]');
      const closeBtn = document.querySelector('[data-si-mobile-menu-close]');
      const menu = document.getElementById('si-mobile-menu');
      const overlay = document.getElementById('si-mobile-overlay');

      if (!menu) return;

      openBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.open(menu, overlay));
      });

      if (closeBtn) closeBtn.addEventListener('click', () => this.close(menu, overlay));
      if (overlay) overlay.addEventListener('click', () => this.close(menu, overlay));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) {
          this.close(menu, overlay);
        }
      });
    },

    open(menu, overlay) {
      menu.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      // Focus first link
      const firstLink = menu.querySelector('a, button');
      if (firstLink) setTimeout(() => firstLink.focus(), 50);
    },

    close(menu, overlay) {
      menu.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    },
  };

  /* ----------------------------------------------------------------
   * 3. NEWSLETTER — success state
   * ---------------------------------------------------------------- */

  const SINewsletter = {
    init() {
      document.querySelectorAll('.si-newsletter-section form').forEach((form) => {
        form.addEventListener('submit', (e) => {
          // Dawn's Shopify form handles submission; we listen for the
          // posted_successfully class or a custom event
        });
      });

      // If Shopify re-renders the page with posted_successfully,
      // show success message (handled server-side in Liquid)
    },
  };

  /* ----------------------------------------------------------------
   * 4. ADD-TO-BASKET feedback
   * Intercepts SI's custom add-to-basket buttons.
   * ---------------------------------------------------------------- */

  const SIBasket = {
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.si-btn-atb[data-si-atb]');
        if (!btn) return;

        const variantId = btn.dataset.siAtb;
        if (!variantId) return;

        e.preventDefault();
        this.addToCart(btn, variantId);
      });
    },

    addToCart(btn, variantId) {
      if (btn.disabled) return;
      btn.disabled = true;

      fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status) {
            // Error — re-enable button
            console.warn('Cart error:', data.description);
            btn.disabled = false;
            return;
          }
          // Success
          btn.classList.add('is-added');
          const originalText = btn.textContent;
          btn.textContent = '✓ Added';

          // Update cart count bubble(s)
          this.refreshCartCount();

          setTimeout(() => {
            btn.classList.remove('is-added');
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);
        })
        .catch(() => {
          btn.disabled = false;
        });
    },

    refreshCartCount() {
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          document.querySelectorAll('.si-basket-count').forEach((el) => {
            el.textContent = cart.item_count;
            el.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
          });
        })
        .catch(() => {});
    },
  };

  /* ----------------------------------------------------------------
   * 5. CATEGORY TILE filter (Books landing page)
   * Live-filter genre tiles by search input
   * ---------------------------------------------------------------- */

  const SITileFilter = {
    init() {
      const input = document.getElementById('si-genre-search');
      if (!input) return;

      input.addEventListener('input', () => {
        const q = input.value.toLowerCase().trim();
        document.querySelectorAll('.si-tile').forEach((tile) => {
          const label = tile.querySelector('.si-tile__label')?.textContent.toLowerCase() || '';
          const desc = tile.querySelector('.si-tile__desc')?.textContent.toLowerCase() || '';
          tile.style.display = !q || label.includes(q) || desc.includes(q) ? '' : 'none';
        });
      });
    },
  };

  /* ----------------------------------------------------------------
   * 6. BOARD GAMES AUDIENCE TOGGLE
   * Filters category tiles by hobbyist / family
   * ---------------------------------------------------------------- */

  const SIAudienceToggle = {
    init() {
      const toggleBtns = document.querySelectorAll('[data-si-audience]');
      if (!toggleBtns.length) return;

      toggleBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const audience = btn.dataset.siAudience;

          // Update button states
          toggleBtns.forEach((b) => b.classList.toggle('is-active', b === btn));

          // Show/hide tiles
          document.querySelectorAll('.si-tile[data-audience]').forEach((tile) => {
            const tileAudience = tile.dataset.audience;
            tile.style.display =
              audience === 'all' || tileAudience === audience || tileAudience === 'both' ? '' : 'none';
          });
        });
      });
    },
  };

  /* ----------------------------------------------------------------
   * 7. INIT
   * ---------------------------------------------------------------- */

  function init() {
    SIMegaMenu.init();
    SIMobileMenu.init();
    SINewsletter.init();
    SIBasket.init();
    SITileFilter.init();
    SIAudienceToggle.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
